/**
 * Company Fetch Coordinator
 * Single point of control for company fetching to prevent race conditions
 * and ensure reliable auto-fetch behavior
 */

import React from 'react';
import { useConnectionStore } from '../stores/connectionStore';

export class CompanyFetchCoordinator {
  private static fetchInProgress = false;
  private static lastFetchTime = 0;
  private static FETCH_COOLDOWN = 10000; // 10 seconds between fetches to prevent loops
  private static listeners: Set<(status: 'started' | 'completed' | 'failed') => void> = new Set();

  /**
   * Add a listener for fetch status changes
   */
  static addListener(callback: (status: 'started' | 'completed' | 'failed') => void) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of fetch status change
   */
  private static notifyListeners(status: 'started' | 'completed' | 'failed') {
    this.listeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.warn('Company fetch listener error:', error);
      }
    });
  }

  /**
   * Check if company fetch is needed and safe to execute
   */
  static shouldFetchCompanies(force = false): {
    should: boolean;
    reason: string;
  } {
    const store = useConnectionStore.getState();
    const now = Date.now();

    // Must be connected to fetch companies
    if (store.status !== 'connected') {
      // Provide specific reasons for different non-connected states
      if (store.status === 'not_connected') {
        return { should: false, reason: 'TallyConnector not connected - please download and install TC from the dashboard' };
      } else if (store.status === 'connecting') {
        return { should: false, reason: 'TallyConnector is connecting - please wait' };
      } else {
        return { should: false, reason: `Not connected to Tally (status: ${store.status})` };
      }
    }

    // Skip if companies already exist and not forcing
    if (store.companies.length > 0 && !force) {
      return { should: false, reason: 'Companies already loaded' };
    }

    // Skip if fetch currently in progress
    if (this.fetchInProgress) {
      return { should: false, reason: 'Fetch already in progress' };
    }

    // Skip if within cooldown period (unless forced)
    if (!force && now - this.lastFetchTime < this.FETCH_COOLDOWN) {
      const remainingCooldown = Math.ceil((this.FETCH_COOLDOWN - (now - this.lastFetchTime)) / 1000);
      return { should: false, reason: `Cooldown period (${remainingCooldown}s remaining)` };
    }

    // Skip if store is currently loading something
    if (store.isLoading && !force) {
      return { should: false, reason: 'Store is currently loading' };
    }

    return { should: true, reason: 'Ready to fetch companies' };
  }

  /**
   * Ensure companies are fetched with coordination and error handling
   */
  static async ensureCompaniesFetched(
    force = false,
    source = 'unknown'
  ): Promise<{
    success: boolean;
    reason: string;
    companiesCount?: number;
  }> {
    const shouldFetch = this.shouldFetchCompanies(force);
    
    if (!shouldFetch.should) {
      console.log(`[CompanyFetch:${source}] Skipping fetch: ${shouldFetch.reason}`);
      return {
        success: false,
        reason: shouldFetch.reason
      };
    }

    console.log(`[CompanyFetch:${source}] Starting company fetch...`);
    
    this.fetchInProgress = true;
    this.lastFetchTime = Date.now();
    this.notifyListeners('started');

    try {
      const store = useConnectionStore.getState();
      await store.refreshWithCompanies();
      
      const updatedState = useConnectionStore.getState();
      const companiesCount = updatedState.companies.length;
      
      console.log(`[CompanyFetch:${source}] ✅ Successfully fetched ${companiesCount} companies`);
      this.notifyListeners('completed');
      
      return {
        success: true,
        reason: 'Companies fetched successfully',
        companiesCount
      };
    } catch (error) {
      console.warn(`[CompanyFetch:${source}] ❌ Fetch failed:`, error);
      this.notifyListeners('failed');
      
      return {
        success: false,
        reason: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      this.fetchInProgress = false;
    }
  }

  /**
   * Get current fetch status
   */
  static getStatus() {
    return {
      fetchInProgress: this.fetchInProgress,
      lastFetchTime: this.lastFetchTime,
      timeSinceLastFetch: Date.now() - this.lastFetchTime,
      canFetch: this.shouldFetchCompanies().should
    };
  }

  /**
   * Reset coordinator state (useful for testing)
   */
  static reset() {
    this.fetchInProgress = false;
    this.lastFetchTime = 0;
    this.listeners.clear();
  }

  /**
   * Auto-fetch hook for components to use (DEPRECATED - use dashboard initialization instead)
   * This method is kept for compatibility but should not be used to prevent loops
   */
  static useAutoFetch(
    enabled = true,
    source = 'component',
    dependencies: any[] = []
  ) {
    console.warn('[CompanyFetchCoordinator] useAutoFetch is deprecated to prevent refresh loops. Use dashboard initialization instead.');
    // Intentionally empty to prevent additional auto-fetch loops
  }
}

export default CompanyFetchCoordinator;